/**
 * Sentinel-scan highlight controller for admin design-page preview bridge.
 *
 * How it works:
 * 1. On the first highlight() call after invalidate(), a scan is performed.
 *    Every CSS var in TOKEN_TO_VARS is temporarily set to a unique sentinel
 *    colour, the DOM is walked, and elements whose computed styles reference
 *    any sentinel are recorded by token.
 * 2. CSS vars are restored and the scan overlay removed.
 * 3. Subsequent highlight(token) calls decorate matching elements with
 *    data-rt-highlight attributes (styled via an injected <style> tag).
 */

// Token → CSS vars it controls. Mirror of applyTheme.ts in this same folder.
// Includes derived vars (--on-primary / --on-accent are functions of primary/accent
// and switch automatically when those change), so a "primary highlight" surfaces
// every element painted with --mint OR drawn on top of it (e.g. button labels).
export const TOKEN_TO_VARS = {
  primary:              ['--mint', '--on-primary'],
  primaryDeep:          ['--mint-deep'],
  primaryTeal:          ['--mint-teal'],
  accent:               ['--hot-pink', '--on-accent'],
  footerGradientFrom:   ['--purple'],
  footerGradientTo:     ['--magenta'],
  headerBarBackground:  ['--header-bar-bg'],
  pageBackground:       ['--cream', '--ash'],
  cardBackground:       ['--paper'],
  textPrimary:          ['--ink'],
  textSecondary:        ['--ink-soft', '--ink-mute'],
  borderColor:          ['--border-color'],
  styleMode:            [],
} as const

export type HighlightToken = keyof typeof TOKEN_TO_VARS

// 12 unique sentinel RGB strings — must be in the format browsers return from
// getComputedStyle (color values are normalised to "rgb(r, g, b)"). Set on the
// CSS var as-is; matched as substrings in computed property values.
const SENTINELS: readonly string[] = [
  'rgb(254, 0, 254)', 'rgb(254, 0, 253)', 'rgb(254, 0, 252)', 'rgb(254, 0, 251)',
  'rgb(254, 0, 250)', 'rgb(254, 0, 249)', 'rgb(254, 0, 248)', 'rgb(254, 0, 247)',
  'rgb(254, 0, 246)', 'rgb(254, 0, 245)', 'rgb(254, 0, 244)', 'rgb(254, 0, 243)',
]

const TOKENS = Object.keys(TOKEN_TO_VARS) as HighlightToken[]

// Tokens that actually drive at least one CSS var. Tokens with empty vars
// (e.g. styleMode is a body data-attr, not a colour) can never match anything
// at scan time, so we exclude them from sentinel assignment to avoid colliding
// modulo-wrap collisions overwriting a real token's match.
const COLOR_TOKENS = TOKENS.filter((t) => TOKEN_TO_VARS[t].length > 0)

if (COLOR_TOKENS.length > SENTINELS.length) {
  throw new Error('previewHighlight: more colour tokens than sentinels — extend SENTINELS')
}

// Map each token-with-vars to one unique sentinel deterministically (by index)
const TOKEN_SENTINEL: Record<HighlightToken, string> = {} as Record<HighlightToken, string>
COLOR_TOKENS.forEach((token, i) => {
  TOKEN_SENTINEL[token] = SENTINELS[i]
})

// All sentinel values as a set for fast lookup
const SENTINEL_SET = new Set(SENTINELS)

// CSS properties to inspect during scan
const SCAN_PROPS = [
  'background-color',
  'color',
  'border-top-color',
  'border-right-color',
  'border-bottom-color',
  'border-left-color',
  'outline-color',
  'fill',
  'stroke',
  'box-shadow',
  'text-decoration-color',
  'background-image',
] as const

const HIGHLIGHT_STYLE_ID = 'rt-highlight-style'
const SCAN_OVERLAY_ID = 'rt-scan-overlay'

const HIGHLIGHT_CSS = `
[data-rt-highlight] {
  outline: 2px solid #C026D3 !important;
  box-shadow: 0 0 0 4px rgba(192, 38, 211, 0.25) !important;
  position: relative;
}
[data-rt-highlight]::after {
  content: attr(data-rt-highlight);
  position: absolute; top: 0; left: 0;
  background: #C026D3; color: #fff;
  font: 600 10px/1 system-ui, sans-serif;
  padding: 2px 6px; border-radius: 0 0 4px 0;
  pointer-events: none; z-index: 2147483646;
}
[data-rt-highlight-active] {
  outline: 3px solid #FFD700 !important;
  z-index: 2147483645 !important;
}
/* Spotlight overlay positioned by JS over the active element. The huge ring +
   9999px box-shadow dims the entire rest of the page so the active match is
   unmistakable even for tiny text nodes that would disappear in a normal
   outline treatment. */
#rt-spotlight {
  position: fixed;
  pointer-events: none;
  border: 6px solid #FFD700;
  border-radius: 10px;
  box-shadow:
    0 0 0 10px rgba(255, 215, 0, 0.55),
    0 0 50px rgba(255, 215, 0, 0.85),
    0 0 0 9999px rgba(0, 0, 0, 0.55);
  z-index: 2147483646;
  animation: rt-spotlight-pulse 1.6s ease-in-out infinite;
}
#rt-spotlight-label {
  position: fixed;
  pointer-events: none;
  background: #FFD700;
  color: #1a1a1a;
  font: 700 12px/1.2 system-ui, sans-serif;
  padding: 4px 10px;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
  z-index: 2147483647;
  white-space: nowrap;
}
@keyframes rt-spotlight-pulse {
  0%, 100% {
    box-shadow:
      0 0 0 10px rgba(255, 215, 0, 0.55),
      0 0 50px rgba(255, 215, 0, 0.85),
      0 0 0 9999px rgba(0, 0, 0, 0.55);
  }
  50% {
    box-shadow:
      0 0 0 16px rgba(255, 215, 0, 0.75),
      0 0 70px rgba(255, 215, 0, 1),
      0 0 0 9999px rgba(0, 0, 0, 0.6);
  }
}
`.trim()

export interface HighlightController {
  highlight(token: HighlightToken): { count: number }
  focus(token: HighlightToken, index: number): { count: number; index: number }
  clear(): void
  invalidate(): void
}

// Structural signature for loop/repeat dedup: tagName + sorted class list.
// Two elements with the same signature are almost always the same component
// emitted by a list .map() (40 menu cards, 96 marquee chips, …) — Tailwind's
// atomic classes make an identical class string a strong "same component" signal.
// `classList` is read (works for HTML and SVG nodes alike, unlike `className`
// which is an SVGAnimatedString object on SVG). Classless elements fall back to a
// parent-context key so unrelated bare nodes in different components are NOT
// merged into one representative.
function componentSignature(el: Element): string {
  const cls = Array.from(el.classList).sort().join(' ')
  if (cls) return `${el.tagName}|${cls}`
  const p = el.parentElement
  const pCls = p ? Array.from(p.classList).sort().join(' ') : ''
  return `${el.tagName}|${p ? p.tagName : ''}>${pCls}`
}

export function createHighlightController(): HighlightController {
  let cache: Map<HighlightToken, Element[]> | null = null
  // Per token: how many raw elements each surviving representative collapsed (>=1).
  // Keyed by token because one element can represent different repeat groups under
  // different tokens (e.g. the mascot <svg> stands for 30 textPrimary nodes AND 6
  // accent nodes). Rebuilt with the cache each scan; read by focus() for the label.
  let repeatCounts = new Map<HighlightToken, WeakMap<Element, number>>()
  let mutationObserver: MutationObserver | null = null
  let debounceTimer: ReturnType<typeof setTimeout> | null = null

  function ensureHighlightStyle() {
    if (document.getElementById(HIGHLIGHT_STYLE_ID)) return
    const style = document.createElement('style')
    style.id = HIGHLIGHT_STYLE_ID
    style.textContent = HIGHLIGHT_CSS
    document.head.appendChild(style)
  }

  function buildCache(): Map<HighlightToken, Element[]> {
    const root = document.documentElement
    repeatCounts = new Map<HighlightToken, WeakMap<Element, number>>()

    // 1. Save current inline values of all CSS vars
    const savedValues = new Map<string, string>()
    for (const token of TOKENS) {
      const vars = TOKEN_TO_VARS[token] as readonly string[]
      for (const cssVar of vars) {
        const inlineVal = root.style.getPropertyValue(cssVar)
        const computedVal = getComputedStyle(root).getPropertyValue(cssVar)
        savedValues.set(cssVar, inlineVal !== '' ? inlineVal : computedVal)
      }
    }

    // 2. Inject scan overlay
    let overlay = document.getElementById(SCAN_OVERLAY_ID)
    if (!overlay) {
      overlay = document.createElement('div')
      overlay.id = SCAN_OVERLAY_ID
      overlay.style.cssText =
        'position:fixed;inset:0;background:#fff;z-index:2147483647;pointer-events:none;'
      document.body.appendChild(overlay)
    }

    // 3. Set every CSS var to its sentinel, force reflow, walk DOM
    for (const token of COLOR_TOKENS) {
      const vars = TOKEN_TO_VARS[token] as readonly string[]
      for (const cssVar of vars) {
        root.style.setProperty(cssVar, TOKEN_SENTINEL[token])
      }
    }
    // Force reflow
    void root.offsetHeight

    const result = new Map<HighlightToken, Element[]>()

    // Per-element per-property sentinel match record, used by the inheritance
    // filter below. A single property can resolve to MORE than one token — e.g. a
    // two-stop gradient whose background-image references both --purple and
    // --magenta — so each property maps to a SET of tokens. (Keying a lone token
    // per property let the second stop overwrite the first, which left
    // footerGradientFrom matching nothing.)
    const elPropMatch = new WeakMap<Element, Map<string, Set<HighlightToken>>>()

    function recordMatch(el: Element, prop: string, token: HighlightToken) {
      let map = elPropMatch.get(el)
      if (!map) {
        map = new Map()
        elPropMatch.set(el, map)
      }
      let tokens = map.get(prop)
      if (!tokens) {
        tokens = new Set()
        map.set(prop, tokens)
      }
      tokens.add(token)
    }

    // Walk EVERY element, including SVG nodes (instanceof Element, not just
    // HTMLElement) so themed fill/stroke on icons and the mascot are scanned —
    // they were silently skipped before, making SVG-painted colours unreachable.
    const allElements = document.body.querySelectorAll('*')
    for (const el of allElements) {
      if (!(el instanceof Element)) continue
      if (el.id === SCAN_OVERLAY_ID) continue
      const computed = getComputedStyle(el)
      for (const prop of SCAN_PROPS) {
        const val = computed.getPropertyValue(prop).toLowerCase()
        if (!val) continue
        for (const sentinel of SENTINEL_SET) {
          if (val.includes(sentinel)) {
            for (const token of COLOR_TOKENS) {
              if (TOKEN_SENTINEL[token] === sentinel) {
                recordMatch(el, prop, token)
              }
            }
          }
        }
      }
    }

    // Inheritance filter: an element only "owns" a token via a property if its
    // closest matching-ancestor does NOT also match the same (token, property).
    // This drops 1000s of textPrimary descendants that just inherit `color`
    // from a single ancestor that explicitly declared it.
    for (const el of allElements) {
      if (!(el instanceof Element)) continue
      const myMap = elPropMatch.get(el)
      if (!myMap) continue

      // Skip elements that are explicitly hidden (display:none / hidden attr).
      // `.hidden` is HTML-only; display:none covers SVG too. We intentionally do
      // NOT use getBoundingClientRect for the visibility check because jsdom (used
      // by unit tests) returns 0x0 for every element — it doesn't perform layout.
      // In real browsers display:none is the signal that actually matters; the
      // chip would not be visible anyway.
      if ((el instanceof HTMLElement && el.hidden) || getComputedStyle(el).display === 'none') continue

      const ownedTokens = new Set<HighlightToken>()
      for (const [prop, tokens] of myMap) {
        for (const token of tokens) {
          let inherited = false
          let parent: Element | null = el.parentElement
          while (parent) {
            const pMap = elPropMatch.get(parent)
            if (pMap && pMap.get(prop)?.has(token)) {
              inherited = true
              break
            }
            parent = parent.parentElement
          }
          if (!inherited) ownedTokens.add(token)
        }
      }

      for (const token of ownedTokens) {
        const existing = result.get(token)
        if (existing) existing.push(el)
        else result.set(token, [el])
      }
    }

    // 4. Restore CSS vars
    for (const token of TOKENS) {
      const vars = TOKEN_TO_VARS[token] as readonly string[]
      for (const cssVar of vars) {
        const saved = savedValues.get(cssVar)
        if (saved !== undefined && saved !== '') {
          root.style.setProperty(cssVar, saved)
        } else {
          root.style.removeProperty(cssVar)
        }
      }
    }

    // Remove overlay
    overlay.remove()

    // Collapse loop/repeat-rendered matches. The inheritance filter above already
    // drops descendants that merely inherit a colour; this collapses SIBLING-style
    // repetition — N identical components from a list .map() (every menu card under
    // cardBackground, every marquee chip under accent, …). Two reductions per match:
    //   1. Unit: a whole <svg> counts as ONE graphic — a mascot/icon's many themed
    //      paths collapse to the <svg> root instead of flooding the list with nodes.
    //   2. Signature: identical units (same tag + sorted classList) collapse to one
    //      representative (first in document order); a parent-context fallback keeps
    //      unrelated classless/bare nodes apart.
    // Each representative remembers how many raw elements it stands for, so the
    // count + ⬆/⬇ navigation reflect distinct UI roles, not data rows, and only
    // representatives get outlined. Measured live: a marquee's 96 accent chips
    // collapse to 1, a menu's 172 textSecondary descriptions to 1, the mascot's
    // 30 textPrimary SVG paths to 1.
    for (const [token, elements] of result) {
      const counts = new WeakMap<Element, number>()
      const seen = new Map<string, Element>()
      const reps: Element[] = []
      for (const el of elements) {
        const unit = el.closest('svg') ?? el
        const sig = componentSignature(unit)
        const rep = seen.get(sig)
        if (rep) {
          counts.set(rep, (counts.get(rep) ?? 1) + 1)
        } else {
          seen.set(sig, unit)
          counts.set(unit, 1)
          reps.push(unit)
        }
      }
      repeatCounts.set(token, counts)
      result.set(token, reps)
    }

    // Sort each token's matches by area ASCENDING so ⬆/⬇ navigation lands
    // on concrete small UI pieces first (a button, a price chip, a single
    // line of text). Giant root wrappers that technically own a colour
    // declaration land at the end of the list — the spotlight on a
    // full-page wrapper would dim the whole iframe and convey nothing.
    for (const [token, elements] of result) {
      elements.sort((a, b) => {
        const ra = a.getBoundingClientRect()
        const rb = b.getBoundingClientRect()
        return (ra.width * ra.height) - (rb.width * rb.height)
      })
      result.set(token, elements)
    }

    return result
  }

  function setupMutationObserver() {
    if (mutationObserver) return
    mutationObserver = new MutationObserver(() => {
      if (debounceTimer !== null) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        invalidate()
      }, 300)
    })
    mutationObserver.observe(document.body, { childList: true, subtree: true })
  }

  function ensureCache(): Map<HighlightToken, Element[]> {
    if (!cache) {
      cache = buildCache()
      setupMutationObserver()
    }
    return cache
  }

  function clearAttrs() {
    document.querySelectorAll('[data-rt-highlight]').forEach((el) => {
      el.removeAttribute('data-rt-highlight')
    })
    document.querySelectorAll('[data-rt-highlight-active]').forEach((el) => {
      el.removeAttribute('data-rt-highlight-active')
    })
    removeSpotlight()
  }

  // Spotlight overlay state — single global pair (overlay + label) per page.
  // Reposition handlers are bound only while a spotlight is active so we don't
  // leak listeners onto every preview-theme call.
  let spotlightTarget: Element | null = null
  let spotlightLabel = ''
  let spotlightRafScheduled = false

  function scheduleSpotlightReposition() {
    if (spotlightRafScheduled) return
    spotlightRafScheduled = true
    requestAnimationFrame(() => {
      spotlightRafScheduled = false
      if (spotlightTarget) positionSpotlight(spotlightTarget, spotlightLabel)
    })
  }

  function onScrollOrResize() {
    scheduleSpotlightReposition()
  }

  function positionSpotlight(el: Element, label: string) {
    const rect = el.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    // Skip drawing the spotlight for absurdly large targets — dimming the
    // entire viewport around something that already fills it conveys nothing
    // and looks like the page just turned grey. The base data-rt-highlight
    // outline still surrounds the element so it remains marked.
    if (rect.width > vw * 0.7 || rect.height > vh * 0.7) {
      removeSpotlightDom()
      spotlightTarget = el
      spotlightLabel = label
      return
    }
    let overlay = document.getElementById('rt-spotlight')
    let labelEl = document.getElementById('rt-spotlight-label')
    if (!overlay) {
      overlay = document.createElement('div')
      overlay.id = 'rt-spotlight'
      document.body.appendChild(overlay)
    }
    if (!labelEl) {
      labelEl = document.createElement('div')
      labelEl.id = 'rt-spotlight-label'
      document.body.appendChild(labelEl)
    }
    // Inflate so tiny inline targets are still surrounded by a clearly
    // visible gold ring (min 24px wide / 18px tall ring radius).
    const padX = Math.max(8, (40 - rect.width) / 2)
    const padY = Math.max(6, (28 - rect.height) / 2)
    const top = Math.max(2, rect.top - padY)
    const left = Math.max(2, rect.left - padX)
    const width = rect.width + padX * 2
    const height = rect.height + padY * 2
    overlay.style.top = `${top}px`
    overlay.style.left = `${left}px`
    overlay.style.width = `${width}px`
    overlay.style.height = `${height}px`
    labelEl.textContent = label
    // Place label above the overlay, snapping below if overlay is at the top.
    const labelTop = top > 30 ? top - 26 : top + height + 6
    labelEl.style.top = `${labelTop}px`
    labelEl.style.left = `${left}px`
    spotlightTarget = el
    spotlightLabel = label
  }

  function removeSpotlightDom() {
    document.getElementById('rt-spotlight')?.remove()
    document.getElementById('rt-spotlight-label')?.remove()
  }
  function removeSpotlight() {
    removeSpotlightDom()
    spotlightTarget = null
    spotlightLabel = ''
    window.removeEventListener('scroll', onScrollOrResize, true)
    window.removeEventListener('resize', onScrollOrResize)
  }

  function highlight(token: HighlightToken): { count: number } {
    ensureHighlightStyle()
    clearAttrs()

    const tokenCache = ensureCache()
    const elements = tokenCache.get(token) ?? []

    for (const el of elements) {
      el.setAttribute('data-rt-highlight', token)
    }

    if (elements.length > 0) {
      focus(token, 0)
    }

    return { count: elements.length }
  }

  function focus(token: HighlightToken, index: number): { count: number; index: number } {
    const tokenCache = ensureCache()
    const elements = tokenCache.get(token) ?? []
    if (elements.length === 0) return { count: 0, index: 0 }
    const safeIdx = ((index % elements.length) + elements.length) % elements.length
    // Clear prior active marker
    document.querySelectorAll('[data-rt-highlight-active]').forEach((el) => {
      el.removeAttribute('data-rt-highlight-active')
    })
    const el = elements[safeIdx]
    el.setAttribute('data-rt-highlight-active', String(safeIdx + 1))
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })

    // Spotlight: position after the scroll animation has had time to settle,
    // then keep it pinned to the element on scroll/resize until cleared.
    // When this representative collapsed N>1 loop repeats, surface "×N" so the
    // designer sees how widespread the colour is without stepping through clones.
    const repeats = repeatCounts.get(token)?.get(el) ?? 1
    const label =
      repeats > 1
        ? `${token} · ${safeIdx + 1}/${elements.length} · ×${repeats}`
        : `${token} · ${safeIdx + 1}/${elements.length}`
    window.addEventListener('scroll', onScrollOrResize, true)
    window.addEventListener('resize', onScrollOrResize)
    // First paint right away (initial position), then again after smooth-scroll.
    positionSpotlight(el, label)
    setTimeout(() => {
      if (document.body.contains(el)) positionSpotlight(el, label)
    }, 380)

    return { count: elements.length, index: safeIdx }
  }

  function clear() {
    clearAttrs()
  }

  function invalidate() {
    cache = null
  }

  return { highlight, focus, clear, invalidate }
}
